import type React from 'react';
import { useMemo } from 'react';

import { useSandbox } from './hooks/useSandbox';
import { useWindowMessage } from './hooks/useWindowMessage';

type SandboxIframeProps = {
	src: string;
	/** a11y — the iframe needs an accessible name. */
	title: string;
};

/**
 * iframe sandbox boundary and container. Wires the fixed sandbox actions to the
 * state machine and renders the iframe; the iframe (a separate document) reaches
 * page state only through those messages. Initial data is baked into `src` by
 * whatever renders this.
 *
 * The child origin messages are trusted from is derived from `src` itself, so
 * whatever renders this is the single source of truth for which child it embeds.
 */
export function SandboxIframe({ src, title }: SandboxIframeProps): React.ReactElement {
	const { actions } = useSandbox();
	const childOrigin = useMemo(() => new URL(src).origin, [src]);

	useWindowMessage('submit', childOrigin, actions.submit);

	useWindowMessage('back', childOrigin, actions.back);

	useWindowMessage('update', childOrigin, actions.update);

	useWindowMessage('track', childOrigin, actions.track);

	return (
		<iframe
			className="h-[calc(100vh-60px)] w-full"
			src={src}
			title={title}
			sandbox="allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	);
}
